import { useEffect, useState } from 'react';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminHeader from '../../components/admin/AdminHeader';
import StatsCard from '../../components/admin/StatsCard';
import { FaHome, FaUsers, FaEnvelope, FaChartLine } from 'react-icons/fa';
import Spinner from '../../components/Spinner';

const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1560184897-ae75f418493e?auto=format&fit=crop&w=800&q=80';

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    totalProperties: 0,
    totalUsers: 0,
    totalContacts: 0,
    recentProperties: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchStats() {
      try {
        // Total properties
        const propertiesSnap = await getDocs(collection(db, 'listings'));
        const totalProperties = propertiesSnap.size;

        // Total users
        const usersSnap = await getDocs(collection(db, 'users'));
        const totalUsers = usersSnap.size;

        // Total contacts
        const contactsSnap = await getDocs(collection(db, 'contactSubmissions'));
        const totalContacts = contactsSnap.size;

        // Recent properties
        const recentQuery = query(
          collection(db, 'listings'),
          orderBy('timestamp', 'desc'),
          limit(5)
        );
        const recentSnap = await getDocs(recentQuery);
        const recentProperties = [];
        recentSnap.forEach((doc) => {
          recentProperties.push({ id: doc.id, ...doc.data() });
        });

        setStats({
          totalProperties,
          totalUsers,
          totalContacts,
          recentProperties,
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar fixed on the left */}
      <AdminSidebar />

      {/* Main content shifted right on lg screens */}
      <div className="flex-1 flex flex-col lg:ml-64">
        <AdminHeader />

        <main className="p-6 space-y-6">
          {/* Top stats */}
          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatsCard
              title="Total Properties"
              value={stats.totalProperties}
              icon={FaHome}
              color="bg-blue-500"
            />
            <StatsCard
              title="Total Users"
              value={stats.totalUsers}
              icon={FaUsers}
              color="bg-green-500"
            />
            <StatsCard
              title="Contact Messages"
              value={stats.totalContacts}
              icon={FaEnvelope}
              color="bg-yellow-500"
            />
            <StatsCard
              title="Overview"
              value="Live"
              icon={FaChartLine}
              color="bg-purple-500"
            />
          </section>

          {/* Recent Properties */}
          <section>
            <h2 className="text-xl font-semibold mb-4">Recent Properties</h2>
            {stats.recentProperties.length === 0 ? (
              <p className="text-gray-600 text-sm">No properties found yet.</p>
            ) : (
              <div className="bg-white shadow rounded-lg overflow-hidden">
                <ul className="divide-y divide-gray-200">
                  {stats.recentProperties.map((property) => {
                    // SAFE IMAGE GUARD
                    const hasImages =
                      property &&
                      Array.isArray(property.imgUrls) &&
                      property.imgUrls.length > 0;
                    const imageUrl = hasImages
                      ? property.imgUrls[0]
                      : FALLBACK_IMAGE;

                    return (
                      <li
                        key={property.id}
                        className="flex items-center p-4 space-x-4 hover:bg-gray-50"
                      >
                        <img
                          src={imageUrl}
                          alt={property.name || 'Property'}
                          className="h-16 w-24 object-cover rounded"
                          onError={(e) => {
                            e.target.src = FALLBACK_IMAGE;
                          }}
                        />
                        <div className="flex-1">
                          <p className="font-semibold text-sm truncate">
                            {property.name || 'Unnamed Property'}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {property.address || 'No address'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-sm text-[#457b9d]">
                            ₹
                            {property.regularPrice
                              ?.toString()
                              .replace(/\B(?=(\d{3})+(?!\d))/g, ',') ||
                              'N/A'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {property.timestamp?.toDate
                              ? property.timestamp
                                  .toDate()
                                  .toLocaleDateString()
                              : 'No date'}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </section>
        </main>
      </div>
    </div>
  );
}
